import { Component, OnInit, Optional } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import {
  PasswordPolicyRule,
  PasswordService
} from '../../services/password.service';
import { AuthService } from '../../services/auth/auth-service';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent implements OnInit {
  /** Policy keys that describe server hashing, not what the user must type. */
  private static readonly suppressedPolicyRuleIds = new Set([
    'hashAlgorithm',
    'hashIterations'
  ]);

  form: FormGroup;
  policyRules: PasswordPolicyRule[] = [];
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  successMessage: string | null = null;
  serverError: string | null = null;
  configError: string | null = null;
  formDisabled = false;
  loading = false;
  /** True after the password-policy request finishes (success or failure). */
  passwordPolicyLoaded = false;

  private static readonly upperPool = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  private static readonly lowerPool = 'abcdefghijklmnopqrstuvwxyz';
  private static readonly digitPool = '0123456789';
  private static readonly specialPool = '!@#$%^&*()-_=+[]{}.,?';

  constructor(
    private readonly fb: FormBuilder,
    private readonly passwordService: PasswordService,
    private readonly auth: AuthService,
    @Optional() private readonly keycloak?: KeycloakService
  ) {
    this.form = this.fb.group(
      {
        currentPassword: ['', Validators.required],
        newPassword: ['', Validators.required],
        confirmPassword: ['', Validators.required]
      },
      { validators: ChangePasswordComponent.passwordMatchValidator }
    );
  }

  ngOnInit(): void {
    this.passwordService.getPasswordPolicy().subscribe({
      next: (rules) => {
        this.policyRules = rules.filter(
          (r) => !ChangePasswordComponent.suppressedPolicyRuleIds.has(r.id)
        );
        this.passwordPolicyLoaded = true;
      },
      error: () => {
        // Policy hints are optional — silently ignore fetch failures
        this.passwordPolicyLoaded = true;
      }
    });
  }

  static passwordMatchValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const newPassword = control.get('newPassword')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      return { passwordMismatch: true };
    }
    return null;
  }

  onSubmit(): void {
    if (this.form.invalid || this.formDisabled) {
      return;
    }

    this.loading = true;
    this.successMessage = null;
    this.serverError = null;

    const { currentPassword, newPassword, confirmPassword } = this.form.value;

    this.passwordService
      .changePassword(currentPassword, newPassword, confirmPassword)
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.successMessage =
            response?.message ||
            $localize`:@@password-changed-successfully:Password changed successfully`;
          this.form.reset();
        },
        error: (error: HttpErrorResponse) => {
          this.loading = false;
          this.handleError(error);
        }
      });
  }

  getPolicyRuleText(rule: PasswordPolicyRule): string {
    const labels: Record<string, string> = {
      length: $localize`:@@policy-min-length:Minimum length`,
      digits: $localize`:@@policy-min-digits:Minimum digits`,
      upperCase: $localize`:@@policy-min-uppercase:Minimum uppercase characters`,
      lowerCase: $localize`:@@policy-min-lowercase:Minimum lowercase characters`,
      specialChars: $localize`:@@policy-min-special:Minimum special characters`,
      notUsername: $localize`:@@policy-not-username:Cannot be the same as your username`,
      notEmail: $localize`:@@policy-not-email:Cannot be the same as your email`,
      passwordHistory: $localize`:@@policy-password-history:Cannot reuse recent passwords`,
      passwordAge: $localize`:@@policy-password-age:Minimum days between password changes`,
      forceExpiredPasswordChange: $localize`:@@policy-force-expired-password:Maximum password lifetime (days)`
    };
    const label = labels[rule.id] || rule.id;
    return rule.value ? `${label}: ${rule.value}` : label;
  }

  togglePasswordAria(visible: boolean): string {
    return visible
      ? $localize`:@@hide-password:Hide password`
      : $localize`:@@show-password:Show password`;
  }

  /**
   * Fills new + confirm password from policy minima. Cannot guarantee server-side
   * rules such as password history; submit may still return policy errors.
   */
  generatePasswordFromPolicy(): void {
    if (this.formDisabled) {
      return;
    }

    const byId = Object.fromEntries(this.policyRules.map((r) => [r.id, r]));
    const minLen = this.parsePolicyInt(byId['length'], 12);
    const minDigit = this.parsePolicyInt(byId['digits'], 0);
    const minUpper = this.parsePolicyInt(byId['upperCase'], 0);
    const minLower = this.parsePolicyInt(byId['lowerCase'], 0);
    const minSpecial = this.parsePolicyInt(byId['specialChars'], 0);

    const compositionalMinimum = minDigit + minUpper + minLower + minSpecial;
    const targetLength = Math.max(minLen, compositionalMinimum, 8);

    const username = this.getUsernameForPolicy();
    const email = this.auth.currentUser$.value?.email ?? undefined;

    for (let attempt = 0; attempt < 80; attempt++) {
      const pwd = this.buildRandomPassword(
        targetLength,
        minDigit,
        minUpper,
        minLower,
        minSpecial
      );
      if (!this.generatedPasswordPassesIdentityRules(pwd, username, email)) {
        continue;
      }
      this.form.patchValue({ newPassword: pwd, confirmPassword: pwd });
      this.form.get('newPassword')?.updateValueAndValidity();
      this.form.get('confirmPassword')?.updateValueAndValidity();
      return;
    }
  }

  toggleVisibility(field: 'current' | 'new' | 'confirm'): void {
    switch (field) {
      case 'current':
        this.showCurrentPassword = !this.showCurrentPassword;
        break;
      case 'new':
        this.showNewPassword = !this.showNewPassword;
        break;
      case 'confirm':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
    }
  }

  private handleError(error: HttpErrorResponse): void {
    const status = error.status;
    const body = error.error;

    switch (status) {
      case 400:
        this.mapFieldErrors(body);
        break;
      case 429:
        this.serverError =
          body?.detail ||
          $localize`:@@too-many-password-attempts:Too many attempts. Please try again later.`;
        break;
      case 501:
        if (body?.code === 'direct_access_grants_disabled') {
          this.configError =
            body?.detail ||
            $localize`:@@password-change-unavailable:Password change is currently unavailable due to server configuration. Please contact your administrator.`;
          this.formDisabled = true;
          this.form.disable();
        }
        break;
      case 502:
        this.serverError =
          body?.detail ||
          $localize`:@@service-unavailable:The service is temporarily unavailable. Please try again later.`;
        break;
      default:
        this.serverError = $localize`:@@unexpected-error:An unexpected error occurred. Please try again.`;
        break;
    }
  }

  private parsePolicyInt(
    rule: PasswordPolicyRule | undefined,
    fallback: number
  ): number {
    if (!rule?.value) {
      return fallback;
    }
    const n = parseInt(String(rule.value), 10);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  }

  private getUsernameForPolicy(): string | undefined {
    const u = this.auth.currentUser$.value;
    if (u?.userName) {
      return u.userName;
    }
    if (this.keycloak) {
      const preferred = this.keycloak.getKeycloakInstance().tokenParsed?.[
        'preferred_username'
      ] as string | undefined;
      if (preferred) {
        return preferred;
      }
    }
    return undefined;
  }

  private randomInt(maxExclusive: number): number {
    if (maxExclusive <= 0) {
      return 0;
    }
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return buf[0] % maxExclusive;
  }

  private pickFromPool(pool: string, count: number, out: string[]): void {
    for (let i = 0; i < count; i++) {
      out.push(pool[this.randomInt(pool.length)]!);
    }
  }

  private shuffleInPlace(chars: string[]): void {
    for (let i = chars.length - 1; i > 0; i--) {
      const j = this.randomInt(i + 1);
      /* eslint-disable security/detect-object-injection --
         Fisher–Yates shuffle; indices i and j are always in range for `chars`. */
      const tmp = chars[i];
      chars[i] = chars[j]!;
      chars[j] = tmp!;
      /* eslint-enable security/detect-object-injection */
    }
  }

  private buildRandomPassword(
    length: number,
    minDigit: number,
    minUpper: number,
    minLower: number,
    minSpecial: number
  ): string {
    const chars: string[] = [];
    this.pickFromPool(ChangePasswordComponent.digitPool, minDigit, chars);
    this.pickFromPool(ChangePasswordComponent.upperPool, minUpper, chars);
    this.pickFromPool(ChangePasswordComponent.lowerPool, minLower, chars);
    this.pickFromPool(ChangePasswordComponent.specialPool, minSpecial, chars);

    const all =
      ChangePasswordComponent.upperPool +
      ChangePasswordComponent.lowerPool +
      ChangePasswordComponent.digitPool +
      ChangePasswordComponent.specialPool;

    while (chars.length < length) {
      chars.push(all[this.randomInt(all.length)]!);
    }
    while (chars.length > length) {
      chars.pop();
    }
    this.shuffleInPlace(chars);
    return chars.join('');
  }

  private generatedPasswordPassesIdentityRules(
    pwd: string,
    username: string | undefined,
    email: string | undefined
  ): boolean {
    const pl = pwd.toLowerCase();
    if (username) {
      const ul = username.toLowerCase();
      if (pl === ul) {
        return false;
      }
      if (ul.length >= 3 && pl.includes(ul)) {
        return false;
      }
    }
    if (email) {
      const el = email.toLowerCase();
      if (pl === el || pl.includes(el)) {
        return false;
      }
    }
    return true;
  }

  private mapFieldErrors(body: any): void {
    this.setFieldError(body?.current_password, 'currentPassword');
    this.setFieldError(body?.new_password, 'newPassword');
    this.setFieldError(body?.confirm_password, 'confirmPassword');
  }

  private setFieldError(value: any, controlName: string): void {
    if (!value) return;
    const message = Array.isArray(value) ? value[0] : value;
    this.form.get(controlName)?.setErrors({ serverError: message });
  }
}
